import React, { useMemo, useState } from "react";
import { Steps, Typography, Input, message, Alert } from "antd";
import { CopyOutlined, LinkOutlined } from "@ant-design/icons";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";

const { Text, Paragraph, Link } = Typography;

/** Scopes to paste into Google Workspace Admin → Domain-wide Delegation */
export const GMAIL_DWD_SCOPES =
  "https://www.googleapis.com/auth/gmail.readonly";

const CONSOLE_LINKS = {
  projects: "https://console.cloud.google.com/projectcreate",
  gmailApi:
    "https://console.cloud.google.com/apis/library/gmail.googleapis.com",
  serviceAccounts: "https://console.cloud.google.com/iam-admin/serviceaccounts",
  dwd: "https://admin.google.com/ac/owl/domainwidedelegation",
  openaiKeys: "https://platform.openai.com/api-keys",
};

function CopyField({ label, value, hint }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      message.success("U kopjua");
    } catch {
      message.error("Kopjimi dështoi — selekto tekstin manualisht");
    }
  };

  return (
    <div className="gmail-copy-field">
      <Text strong className="field-label">
        {label}
      </Text>
      {hint ? <Paragraph className="field-hint">{hint}</Paragraph> : null}
      <div className="gmail-copy-field__controls">
        <Input value={value} readOnly />
        <MondayButton
          className="mondayButtonBlue"
          icon={<CopyOutlined />}
          onClick={copy}
        >
          Kopjo
        </MondayButton>
      </div>
    </div>
  );
}

function ExternalLink({ href, children }) {
  return (
    <Link href={href} target="_blank" rel="noreferrer">
      <LinkOutlined /> {children}
    </Link>
  );
}

/**
 * Step-by-step Google Cloud / Workspace + OpenAI setup.
 * Values to paste INTO Google Console are copyable; values FROM Google go into parent form fields.
 */
const GmailSetupWizard = ({
  saJson,
  onSaJsonChange,
  delegatedMailbox,
  onDelegatedMailboxChange,
  openaiKey,
  onOpenaiKeyChange,
  hasServiceAccountJson,
  hasOpenAiKey,
}) => {
  const [step, setStep] = useState(0);

  const clientEmail = useMemo(() => {
    try {
      if (!saJson?.trim()) return "";
      return JSON.parse(saJson).client_email || "";
    } catch {
      return "";
    }
  }, [saJson]);

  const clientId = useMemo(() => {
    try {
      if (!saJson?.trim()) return "";
      return String(JSON.parse(saJson).client_id || "");
    } catch {
      return "";
    }
  }, [saJson]);

  const items = [
    {
      title: "Projekt",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            Hap Google Cloud Console dhe krijo (ose zgjidh) një projekt për
            FlexHR.
          </Paragraph>
          <ExternalLink href={CONSOLE_LINKS.projects}>
            Hap Create Project
          </ExternalLink>
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Nuk ka vlerë për të kopjuar këtu — vetëm krijoni projektin dhe mbani emrin e tij."
          />
        </div>
      ),
    },
    {
      title: "Gmail API",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            Në të njëjtin projekt, aktivizo <Text code>Gmail API</Text>.
          </Paragraph>
          <ExternalLink href={CONSOLE_LINKS.gmailApi}>
            Hap Gmail API Library
          </ExternalLink>
          <Paragraph className="field-hint" style={{ marginTop: 12 }}>
            Kliko <Text strong>Enable</Text>. Pastaj vazhdo te Service Account.
          </Paragraph>
        </div>
      ),
    },
    {
      title: "Service Account",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            1. Krijo një Service Account (p.sh.{" "}
            <Text code>flexhr-gmail-inbox</Text>).
            <br />
            2. Te Keys → Add key → Create new key → <Text strong>JSON</Text>.
            <br />
            3. Ngjit të gjithë përmbajtjen e file-it JSON më poshtë.
          </Paragraph>
          <ExternalLink href={CONSOLE_LINKS.serviceAccounts}>
            Hap Service Accounts
          </ExternalLink>
          <div style={{ marginTop: 12 }}>
            <Text className="field-label">
              Service account JSON{" "}
              {hasServiceAccountJson && !saJson?.trim() ? (
                <Text className="status-ok">
                  (i ruajtur mëngjër — lije bosh ose zëvendëso)
                </Text>
              ) : null}
            </Text>
            <Input.TextArea
              style={{ marginTop: 6 }}
              rows={6}
              placeholder='{"type":"service_account","project_id":"…","private_key_id":"…","private_key":"…","client_email":"…","client_id":"…",…}'
              value={saJson}
              onChange={(e) => onSaJsonChange(e.target.value)}
            />
          </div>
          {clientEmail ? (
            <div style={{ marginTop: 12 }}>
              <CopyField
                label="client_email (nga JSON — për referencë)"
                value={clientEmail}
                hint="Ky email duhet të shfaqet në Google Cloud → Service Account."
              />
            </div>
          ) : null}
          {clientId ? (
            <div style={{ marginTop: 12 }}>
              <CopyField
                label="client_id (kopjo në Google Admin DWD)"
                value={clientId}
                hint="Hapi tjetër: ngjite këtë Client ID te Domain-wide Delegation."
              />
            </div>
          ) : (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="Ngjit JSON-in e vlefshëm që të lexohen client_email dhe client_id automatikisht."
            />
          )}
        </div>
      ),
    },
    {
      title: "DWD",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            Hap <Text strong>Google Workspace Admin</Text> (jo Cloud Console) →
            Security → API controls → Domain-wide Delegation →{" "}
            <Text strong>Add new</Text>.
          </Paragraph>
          <ExternalLink href={CONSOLE_LINKS.dwd}>
            Hap Domain-wide Delegation
          </ExternalLink>
          <div style={{ marginTop: 12 }}>
            <CopyField
              label="Client ID (nga Google Console / JSON)"
              value={clientId || "(ngjit JSON në hapin e mëparshëm)"}
              hint="Fusha: Client ID në Admin Console."
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <CopyField
              label="OAuth scopes (ngjit te Admin Console)"
              value={GMAIL_DWD_SCOPES}
              hint="Vetëm gmail.readonly — mjafton për leximin e inbox-it."
            />
          </div>
          <Alert
            style={{ marginTop: 12 }}
            type="info"
            showIcon
            message="Pas ruajtjes në Admin, prit 1–5 minuta që delegimi të aktivizohet."
          />
        </div>
      ),
    },
    {
      title: "Mailbox",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            Email-i i Workspace që FlexHR do të lexojë (p.sh. inbox i HR).
            Service account impersonon këtë mailbox.
          </Paragraph>
          <Text className="field-label">Mailbox</Text>
          <Input
            placeholder="hr@flex.al"
            value={delegatedMailbox}
            onChange={(e) => onDelegatedMailboxChange(e.target.value)}
          />
        </div>
      ),
    },
    {
      title: "OpenAI",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            Sync përdor OpenAI për të nxjerrë datat / punonjësin nga email-i.
            Çelësi ruhet në AWS SSM (si JSON i Gmail) — <Text strong>jo</Text>{" "}
            në file lokal <Text code>.env</Text> të browser-it.
          </Paragraph>
          <ExternalLink href={CONSOLE_LINKS.openaiKeys}>
            Hap OpenAI API keys
          </ExternalLink>
          <div style={{ marginTop: 12 }}>
            <Text className="field-label">
              OpenAI API key{" "}
              {hasOpenAiKey && !openaiKey?.trim() ? (
                <Text className="status-ok">
                  (i konfiguruar — lije bosh për ta mbajtur)
                </Text>
              ) : (
                <Text className="status-warn">
                  (mungon ose do zëvendësohet)
                </Text>
              )}
            </Text>
            <Input.Password
              style={{ marginTop: 6 }}
              placeholder="sk-…"
              value={openaiKey}
              onChange={(e) => onOpenaiKeyChange(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <Paragraph className="field-hint" style={{ marginTop: 12 }}>
            Alternativë ops: vendos SSM{" "}
            <Text code>/flexhr/&lt;stage&gt;/openai/apiKey</Text> me AWS CLI —
            e njëjta vlerë që shkruan këtu.
          </Paragraph>
        </div>
      ),
    },
    {
      title: "Ruaj",
      content: (
        <div className="gmail-wizard-step">
          <Paragraph>
            Kliko <Text strong>Ruaj</Text> më poshtë në faqen e konfigurimit,
            pastaj <Text strong>Testo lidhjen</Text> dhe{" "}
            <Text strong>Sync tani</Text>.
          </Paragraph>
          <Alert
            type="success"
            showIcon
            message="Checklist"
            description={
              <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                <li>Gmail API enabled</li>
                <li>JSON service account i ngjitur / i ruajtur</li>
                <li>DWD me Client ID + scope gmail.readonly</li>
                <li>Mailbox e deleguar e plotësuar</li>
                <li>OpenAI key i ruajtur</li>
                <li>Aktiv = ON</li>
              </ul>
            }
          />
        </div>
      ),
    },
  ];

  const stepItems = items.map((i) => ({ title: i.title }));

  return (
    <div className="gmail-setup-wizard">
      <Steps
        className="gmail-wizard-steps gmail-wizard-steps--desktop"
        current={step}
        onChange={setStep}
        size="small"
        responsive={false}
        items={stepItems}
      />
      <div className="gmail-wizard-progress-mobile" aria-live="polite">
        <Text strong>
          Hapi {step + 1}/{items.length}
        </Text>
        <Text className="gmail-wizard-progress-mobile__title">
          {items[step].title}
        </Text>
        <div
          className="gmail-wizard-progress-mobile__bar"
          role="progressbar"
          aria-valuenow={step + 1}
          aria-valuemin={1}
          aria-valuemax={items.length}
        >
          <span
            style={{
              width: `${((step + 1) / items.length) * 100}%`,
            }}
          />
        </div>
      </div>
      <div className="gmail-wizard-body">{items[step].content}</div>
      <div className="gmail-wizard-nav">
        <MondayButton
          className="mondayButtonBlue"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          Mbrapa
        </MondayButton>
        <MondayButton
          className="mondayButtonGreen"
          disabled={step >= items.length - 1}
          onClick={() => setStep((s) => Math.min(items.length - 1, s + 1))}
        >
          Para
        </MondayButton>
      </div>
    </div>
  );
};

export default GmailSetupWizard;
